import { useMemo } from 'react'

import cn from 'classnames'

import { Option, OptionGroup } from '@legacy/types/SelectorValues'
import { prepareOptionsForGroupedOptions } from '@legacy/utils/productInfoHelpers'

import Container from './Container'
import style from './FloatingLabelSelect.module.scss'
import Root from './Root'
import SearchableSelect from './SearchableSelect'
import { FloatingLabelSelectProps, LabelProps, SelectedItemProps, SelectProps } from './types'

/**
 * A dropdown that matches our floating label input in style
 *
 * @example
 * Here are some example props:
 *
 *     {
 *       "options": [
 *         {
 *           "text": "Commercial drone pilot",
 *           "value": "Commercial drone pilot"
 *         },
 *         {
 *           "text": "Commercial drone owner and operator",
 *           "value": "Commercial drone owner and operator"
 *         },
 *         {
 *           "text": "Other",
 *           "value": "Other"
 *         }
 *       ],
 *       "onChange": (event, ??) => any,
 *       "onBlur": (event) => any,
 *       "search": false,
 *       "data-testid": "businessType",
 *       "name": "businessType",
 *       "value": ""
 *     }
 */

const FloatingLabelSelect = ({
  label,
  labelColor,
  blankLabel,
  onChange,
  onBlur,
  options,
  placeholder,
  required = false,
  value,
  invalid = false,
  touched = false,
  disabled,
  name,
  searchable,
  groupBy,
  'data-testid': dataTestId,
  ...selectProps
}: FloatingLabelSelectProps) => {
  // The currently-selected item
  const selected = options.find((option: Option) => option.value === value)

  // The label of the currently-selected item
  const valueLabel: string = selected ? selected.text : ''

  return (
    <Container>
      <Root
        data-testid={dataTestId || name}
        invalid={!!invalid && !!touched}
        selected={!!selected}
        disabled={disabled}
        className={cn({
          [style.root]: true,
          [style.hasValue]: !!selected,
        })}
      >
        {searchable ? (
          <SearchableSelect {...{ onChange, onBlur, options, disabled, label, invalid, value, groupBy }} />
        ) : (
          <>
            {/* The field label */}
            <Label {...{ label, labelColor, required }} />

            {/* The name of the value */}
            <SelectedItem selected={!!selected} {...{ placeholder, valueLabel, disabled }} />

            {/* The dropdown */}
            <Select {...{ blankLabel, onChange, onBlur, options, ...selectProps, value, disabled, name, groupBy }} />
          </>
        )}
      </Root>
    </Container>
  )
}

/**
 * The field label and the 'requried' star
 */

const Label = ({ label, labelColor }: LabelProps) => (
  <span className={cn(style.label, 'floatingLabelSelectLabel')} style={{ color: labelColor }}>
    {label}
  </span>
)

/**
 * The label for a selected item, or the placeholder (if available)
 */

const SelectedItem = ({ placeholder, selected, valueLabel, disabled }: SelectedItemProps) =>
  selected ? (
    <span className={cn(style.value, { [style.disabled]: disabled })} key={valueLabel}>
      {valueLabel}
    </span>
  ) : placeholder ? (
    <span className={style.value}>{placeholder}</span>
  ) : null

/**
 * The invisible dropdown element
 */

const Select = ({ blankLabel, onChange, onBlur, options, value, disabled, groupBy }: SelectProps) => (
  <select className={style.select} value={value} onChange={onChange} onBlur={onBlur} disabled={disabled}>
    {value ? null : <option value="">{blankLabel}</option>}
    {groupBy ? <OptionsGroupMapped groupBy={groupBy} options={options} /> : <OptionsMapped options={options} />}
  </select>
)

const OptionsMapped = ({ options }: { options: Option[] }) => (
  <>
    {options.map(({ value, text }) => (
      <option key={value} value={value}>
        {text}
      </option>
    ))}
  </>
)

const OptionsGroupMapped = ({ options, groupBy }: { options: Option[]; groupBy: string }) => {
  const optionGroups: OptionGroup[] = useMemo(
    () => prepareOptionsForGroupedOptions(groupBy, options),
    [groupBy, options],
  )
  return (
    <>
      {optionGroups.map(({ text, options }) => (
        <optgroup key={text} label={text}>
          {options.map(({ value, text }) => (
            <option key={value} value={value}>
              {text}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  )
}

export default FloatingLabelSelect
